import { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { format } from 'date-fns';

import { Grid, Heading, Text } from '@simplywallst/ui-core';
import { SWS_HOST } from '@/constants';
import DocumentHeader from '@components/DocumentHeader';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import Breadcrumb from '@/components/Breadcrumb';
import { ImageHeader } from '@/components/Header';

import { ShareAnnouncement } from '../Announcement';
import { makeBreadcrumbsFromCanonicalUrl } from '../../utils';
import { useAuthorProfileImage, useDescription } from '../../hooks';
import type { Author, Company } from '../../hooks/useNarrative/types';
import { FollowNarrative } from '../FollowNarrative';

import { AuthorImage, StyledLink } from './styled';
import NarrativeVoting from '@/pages/Narrative/components/NarrativeVoting';
import useNarrative from '@/pages/Narrative/hooks/useNarrative';

interface Props {
  title: string;
  coverImageUrl: string;
  author: Author;
  company: Company;
  publishedAt: string;
  updatedAt: string;
  slug: string;
  announcementCount: number;
  children?: ReactNode;
}

export const Header = ({
  title,
  coverImageUrl,
  author,
  publishedAt,
  updatedAt,
  company,
  slug,
  announcementCount,
  children,
}: Props) => {
  const {
    data: { voteCount, id },
  } = useNarrative();
  const description = useDescription();
  const { trackEvent } = useTracking();
  const coverImageSrc = useCloudflareImage({
    src: coverImageUrl,
    width: 720,
    height: 152,
    fit: 'cover',
  });

  const authorImageUrl = useAuthorProfileImage({
    src: author.imageUrl,
    width: 32,
    height: 32,
  });

  const breadcrums = makeBreadcrumbsFromCanonicalUrl({
    canonicalUrl: company.canonicalURL,
    companyName: company.name,
  });
  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: coverImageUrl,
    breakpoints: [
      { width: 1024, height: 152 },
      { width: 1023, height: 135 },
      { width: 768, height: 135, fit: 'contain' },
      { width: 425, height: 135, fit: 'contain' },
      { width: 375, height: 135, fit: 'contain' },
    ],
  });
  return (
    <Grid as="header" gap="16px">
      <DocumentHeader
        title={title}
        allowCrawlers
        openGraphImageUrl={coverImageUrl}
        description={description}
      >
        <link rel="canonical" href={`${SWS_HOST}/narratives/${slug}`} />
      </DocumentHeader>
      <ImageHeader
        imageSource={coverImageSrc}
        imageSourceSet={coverImageSrcSet}
      >
        <Grid gap="16px">
          <Breadcrumb list={breadcrums} data-cy-id="narrative-breadcrumb" />
          <Heading
            as="h1"
            typography="megaStrong"
            data-cy-id="narrative-heading"
          >
            {title}
          </Heading>
          <Grid
            gap="8px"
            templateColumnsMd="max-content max-content"
            justifyContent="space-between"
          >
            <Grid gap="8px" templateColumnsSm="max-content max-content">
              <Grid
                gap="8px"
                templateColumns="max-content max-content"
                alignItems="center"
              >
                <StyledLink
                  href={author.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    trackEvent({
                      modifier: 'author',
                      action: 'click',
                      announcementCount,
                    })
                  }
                >
                  <Grid
                    gap="8px"
                    templateColumns="32px max-content"
                    alignItems="center"
                  >
                    <AuthorImage src={authorImageUrl} />
                    <Text
                      typography="small"
                      color="white"
                      data-cy-id="narrative-author-name"
                    >
                      {author.name}
                    </Text>
                  </Grid>
                </StyledLink>
                <Text
                  typography="tiny"
                  color="mediumGray"
                  data-cy-id="narrative-author-role"
                >
                  {author.role}
                </Text>
              </Grid>
              <Grid gap="8px" templateColumns="max-content max-content">
                <Grid
                  gap="8px"
                  templateColumns="max-content max-content"
                  alignItems="center"
                >
                  <Text typography="tiny" color="paperRGBA" opacity={0.7}>
                    Published
                  </Text>
                  <Text
                    typography="tiny"
                    color="paper"
                    data-cy-id="narrative-published-date"
                  >
                    {format(new Date(publishedAt), 'MMMM dd yyyy')}
                  </Text>
                </Grid>
                {updatedAt && (
                  <Grid
                    gap="8px"
                    templateColumns="max-content max-content"
                    alignItems="center"
                  >
                    <Text typography="tiny" color="paperRGBA" opacity={0.7}>
                      Updated
                    </Text>
                    <Text
                      typography="tiny"
                      color="paper"
                      data-cy-id="narrative-updated-date"
                    >
                      {format(new Date(updatedAt), 'MMMM dd yyyy')}
                    </Text>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid gap="8px" autoFlow="column" maxWidth="max-content">
              <NarrativeVoting
                narrativeId={id}
                slug={slug}
                upVotes={voteCount.upvotes}
                downVotes={voteCount.downvotes}
              />
              <ShareAnnouncement />
              <FollowNarrative />
            </Grid>
          </Grid>
          {children}
        </Grid>
      </ImageHeader>
    </Grid>
  );
};
